import { Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { TopSupplier } from '../../../services/dashboard.interface';

const COLORS = ['#2E6BFF', '#44C3C6', '#FFD54D', '#FF8A3B', '#FF5A5A'];

interface DonutItem {
  name: string;
  value: number;
  color: string;
  pct: number;
  startAngle: number;
  endAngle: number;
}

@Component({
  selector: 'app-top-suppliers-donut',
  imports: [CommonModule, MatCardModule, TranslateModule],
  templateUrl: './top-suppliers-donut.html',
  styleUrl: './top-suppliers-donut.css',
})
export class TopSuppliersDonutComponent {
  @Input() title: string = '';
  @Input() set suppliers(value: TopSupplier[] | TopSupplier | null | undefined) {
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    this._suppliers.set(arr);
  }

  private _suppliers = signal<TopSupplier[]>([]);

  data = computed(() => {
    const suppliers = this._suppliers();
    const raw = suppliers
      .map(s => ({ name: s.companyName ?? '—', value: s.totalAllocated ?? 0 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const total = raw.reduce((acc, d) => acc + d.value, 0);

    if (total === 0) {
      return {
        items: [{ name: '—', value: 0, color: COLORS[0], pct: 0, startAngle: 0, endAngle: 360 }],
        total: 0
      };
    }

    let currentAngle = 0;
    const items: DonutItem[] = raw.map((d, i) => {
      const pct = (d.value / total) * 100;
      const angleSpan = (pct / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angleSpan;
      currentAngle = endAngle;

      return {
        ...d,
        color: COLORS[i % COLORS.length],
        pct,
        startAngle,
        endAngle
      };
    });

    return { items, total };
  });

  kgToTons(kg: number): string {
    if (kg === 0) return '0';
    const tons = kg / 1000;
    return tons.toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  }

  describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
    let arcAngle = endAngle - startAngle;
    if (arcAngle < 0) arcAngle += 360;

    // Для полного круга или почти полного (≥359.9°) рисуем две дуги
    if (arcAngle >= 359.9) {
      const mid = startAngle + 180;
      const midPoint = this.polarToCartesian(x, y, radius, mid);
      const start = this.polarToCartesian(x, y, radius, startAngle);
      const end = this.polarToCartesian(x, y, radius, endAngle);

      return [
        'M', start.x, start.y,
        'A', radius, radius, 0, 1, 1, midPoint.x, midPoint.y,
        'A', radius, radius, 0, 1, 1, end.x, end.y
      ].join(' ');
    }

    const start = this.polarToCartesian(x, y, radius, startAngle);
    const end = this.polarToCartesian(x, y, radius, endAngle);
    const largeArcFlag = arcAngle > 180 ? '1' : '0';

    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y
    ].join(' ');
  }

  private polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = angleInDegrees * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  }

  getLabelPosition(item: DonutItem, radius: number, centerX: number, centerY: number): {x: number, y: number} {
    const midAngle = (item.startAngle + item.endAngle) / 2;
    const offset = 40;
    const r = radius + offset;
    const angleInRadians = midAngle * Math.PI / 180.0;

    return {
      x: centerX + (r * Math.cos(angleInRadians)),
      y: centerY + (r * Math.sin(angleInRadians))
    };
  }
}
